import { useState, useEffect } from 'react'
import { DivPaperStyled, ModalStyled } from '../Modals.styled'
import { Backdrop, Fade, MenuItem, TextField } from '@mui/material'
import { KTSVG } from '../../../_metronic/helpers'
import { modalStyles } from '../ModalStyles'
import { RootState } from '../../../setup'
import { useSelector } from 'react-redux'
import DateInput from '../LTLandVolumeTLPages/DateInput'
import { InputStyled } from '../Inputs.styled'
import { useStyles } from '../LTLandVolumeTLPages/FreightInfo/FreightInformation/FreightInformationStyles'
import { stylesMUIInput } from '../stylesMUIInput'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

interface AppointmentModalProps {
    open: boolean
    setOpen: (open: boolean) => void
    type: 'Pickup' | 'Delivery'
    formik: any
    formDisabled?: boolean
}

const AppointmentModal = ({ open, setOpen, type, formik, formDisabled }: AppointmentModalProps) => {
    const classes = modalStyles({})
    const selectClasses = useStyles({props: {}})
    const fontSize = useSelector<RootState>(({pages}) => pages.fontSize) as number
    const menuProps = { classes: { list: selectClasses.selectPaperList } }
    const fieldPrefix = type === 'Pickup' ? 'pickupDate' : 'deliveryDate'
    const values = formik.values[fieldPrefix]

    const [localValues, setLocalValues] = useState({
        appointmentDate: null,
        appointmentTime: null,
        appointmentNumber: '',
        appointmentContact: '',
        appointmentNotes: ''
    })

    const baseTimeOptions = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00']

    const timeOptions = localValues.appointmentTime && !baseTimeOptions.includes(localValues.appointmentTime)
        ? [...baseTimeOptions, localValues.appointmentTime].sort()
        : baseTimeOptions

    useEffect(() => {
        if (open) {
            setLocalValues({
                appointmentDate: values?.appointmentDate || null,
                appointmentTime: normalizeTime(values?.appointmentTime),
                appointmentNumber: values?.appointmentNumber || '',
                appointmentContact: values?.appointmentContact || '',
                appointmentNotes: values?.appointmentNotes || ''
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const isValidTime = (val: any) => val && val !== '0000' && val !== '00:00' && val !== '00:00:00'

    // Normalize time from backend formats (e.g. "15:00:00", "1500") to "HH:mm"
    const normalizeTime = (val: any): string | null => {
        if (!isValidTime(val)) return null
        if (typeof val === 'string') {
            // "15:00:00" -> "15:00"
            if (val.length > 5 && val.includes(':')) return val.substring(0, 5)
            // "1500" -> "15:00"
            if (val.length === 4 && !val.includes(':')) return val.slice(0, 2) + ':' + val.slice(2)
        }
        return val
    }

    const handleSave = () => {
        const cleanTime = normalizeTime(localValues.appointmentTime)
        const hasData = !!(localValues.appointmentDate || cleanTime || localValues.appointmentNumber || localValues.appointmentContact || localValues.appointmentNotes)
        formik.setFieldValue(`${fieldPrefix}.appointmentNeeded`, hasData)
        formik.setFieldValue(`${fieldPrefix}.appointmentDate`, localValues.appointmentDate)
        formik.setFieldValue(`${fieldPrefix}.appointmentTime`, cleanTime)
        formik.setFieldValue(`${fieldPrefix}.appointmentNumber`, localValues.appointmentNumber)
        formik.setFieldValue(`${fieldPrefix}.appointmentContact`, localValues.appointmentContact)
        formik.setFieldValue(`${fieldPrefix}.appointmentNotes`, localValues.appointmentNotes)
        setOpen(false)
    }

    const handleCancel = () => {
        setOpen(false)
    }

    return (
        <ModalStyled
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className={classes.modal}
            fontSize={fontSize}
            open={open}
            onClose={handleCancel}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <DivPaperStyled className={classes.paper}>
                    {/* Header */}
                    <div className="row modal-header p-0 pb-2">
                        <div className="col-10 d-flex align-items-center">
                            <h5 className="modal-title">
                                {type === 'Pickup' ? 'Pickup' : 'Delivery'} Appointment
                            </h5>
                        </div>
                        <div className="col-2 d-flex justify-content-end">
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary"
                                onClick={handleCancel}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <>
                        {/* Date & Time */}
                        <div className="row align-items-center mb-7 mt-5">
                            <div className="col-12 col-sm-2">
                                <label className="form-label mb-0">Date</label>
                            </div>
                            <div className="col-12 col-sm-4 mb-5 mb-sm-0">
                                <DateInput
                                    setDataFormik={(name, value) => {
                                        setLocalValues({ ...localValues, appointmentDate: value })
                                    }}
                                    pickupDate={localValues.appointmentDate}
                                    error=""
                                    touched={false}
                                    setTouched={() => {}}
                                    name="appointmentDate"
                                    allowPastDates={true}
                                    formDisabled={formDisabled}
                                />
                            </div>
                            <div className="col-12 col-sm-2">
                                <label className="form-label mb-0">Time</label>
                            </div>
                            <div className="col-12 col-sm-4">
                                <TextField
                                    select
                                    label=" "
                                    value={localValues.appointmentTime || ''}
                                    onChange={(e) =>
                                        setLocalValues({ ...localValues, appointmentTime: e.target.value || null })
                                    }
                                    disabled={formDisabled}
                                    className={`${selectClasses.selectMUI} w-100`}
                                    InputProps={{
                                        style: stylesMUIInput(fontSize),
                                    }}
                                    SelectProps={{
                                        IconComponent: KeyboardArrowDownIcon,
                                        MenuProps: menuProps,
                                        displayEmpty: true
                                    }}
                                    InputLabelProps={{shrink: false}}
                                >
                                    <MenuItem value="">Select time</MenuItem>
                                    {timeOptions.map((t) => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        </div>

                        {/* Appt # */}
                        <div className="row align-items-center mb-7">
                            <div className="col-12 col-sm-2">
                                <label className="form-label mb-0">Appt #</label>
                            </div>
                            <div className="col-12 col-sm-10">
                                <InputStyled
                                    type="text"
                                    className="form-control"
                                    autoComplete="off"
                                    value={localValues.appointmentNumber}
                                    onChange={(e) =>
                                        setLocalValues({
                                            ...localValues,
                                            appointmentNumber: e.target.value,
                                        })
                                    }
                                    maxLength={100}
                                    disabled={formDisabled}
                                    fontSize={fontSize}
                                />
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="row align-items-center mb-7">
                            <div className="col-12 col-sm-2">
                                <label className="form-label mb-0">Contact</label>
                            </div>
                            <div className="col-12 col-sm-10">
                                <InputStyled
                                    type="text"
                                    className="form-control"
                                    autoComplete="off"
                                    value={localValues.appointmentContact}
                                    onChange={(e) =>
                                        setLocalValues({
                                            ...localValues,
                                            appointmentContact: e.target.value,
                                        })
                                    }
                                    maxLength={100}
                                    disabled={formDisabled}
                                    fontSize={fontSize}
                                />
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="row align-items-center mb-7">
                            <div className="col-12 col-sm-2">
                                <label className="form-label mb-0">Remarks</label>
                            </div>
                            <div className="col-12 col-sm-10">
                                <textarea
                                    style={{ height: 60 }}
                                    className="form-control"
                                    rows={2}
                                    autoComplete="off"
                                    value={localValues.appointmentNotes}
                                    onChange={(e) =>
                                        setLocalValues({
                                            ...localValues,
                                            appointmentNotes: e.target.value,
                                        })
                                    }
                                    disabled={formDisabled}
                                />
                            </div>
                        </div>
                    </>

                    {/* Footer */}
                    <div className="row mb-3">
                        <div className="col-12 d-flex justify-content-end">
                            <button
                                className="btn btn-secondary me-4"
                                onClick={handleCancel}
                            >
                                Close
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={formDisabled}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </DivPaperStyled>
            </Fade>
        </ModalStyled>
    )
}

export default AppointmentModal
